import SurveyCreationWizard from './SurveyCreationWizard';
import React from 'react';
import {
  Box,
  Alert,
  Button,
  Chip,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import PollIcon from '@mui/icons-material/Poll';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import BarChartIcon from '@mui/icons-material/BarChart';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import StackedBarChartIcon from '@mui/icons-material/StackedBarChart';
import IosShareIcon from '@mui/icons-material/IosShare';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DeleteIcon from '@mui/icons-material/Delete';
import { Analytics, Comment } from '@mui/icons-material';

import * as ETVConstants from '../ETVConstants';
import ETVPage from '../tools/ETVPage';
import ETVTable from '../tools/ETVTable';
import ETVMessagePopup from '../tools/ETVMessagePopup';
import ETVPopup from '../tools/ETVPopup';
import ETVActionButton from '../tools/ETVActionButton';

import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as ReTooltip,
  CartesianGrid,
  LabelList,
  PieChart,
  Pie,
  Legend,
  Cell,
} from 'recharts';

const COLORS = ['#0072B2', '#E69F00', '#009E73', '#D55E00', '#CC79A7', '#56B4E9'];

class SurveyMgmt extends React.Component {
  constructor(props) {
    super(props);
    this.analysisRef = React.createRef();
  }

  state = {
    // data
    items: [],
    locations: [],
    data: undefined,

    // ui
    window: '',
    isLoading: false,

    // message dialog
    info_type: 'info',
    info_message: '',
    info_title: '',
    spinning: false,

    selected_language: 'all',

    // analysis / comments
    chart_mode: 'bar',
    cur_survey_id: undefined,
    question: '',
    survey_comments: [],
    pending_delete: null,
  };

  componentDidMount() {
    this.loadItems();
  }

  // ---------- helpers ----------
  getItem(id) {
    return (this.state.items || []).find((it) => it.id === id);
  }

  getChartRows() {
    const rows = this.state.data?.slice(1) || [];
    return rows.map(([name, hits]) => ({ name: String(name ?? ''), hits: Number(hits ?? 0) }));
  }

  getTotalHits() {
    return this.getChartRows().reduce((sum, r) => sum + (r.hits || 0), 0);
  }

  getDataMax() {
    return this.getChartRows().reduce((m, r) => Math.max(m, Number(r.hits || 0)), 0);
  }

  getLeader() {
    const rows = this.getChartRows();
    if (!rows.length) return null;
    return rows.reduce((max, r) => (r.hits > max.hits ? r : max), rows[0]);
  }

  toggleChartMode() {
    this.setState((s) => ({ chart_mode: s.chart_mode === 'bar' ? 'pie' : 'bar' }));
  }

  // ---------- load ----------
  loadItems() {
    const accessToken = localStorage.getItem('etv.access_token');
    const reqUserID = localStorage.getItem('etv.user_id');

    let url = `${ETVConstants.getServerBase()}/survey_mgmt?user_id=${this.props.user_id}`;
    if (this.state.selected_language && this.state.selected_language !== 'all') {
      url += `&country=${encodeURIComponent(this.state.selected_language)}`;
    }

    this.setState({ isLoading: true });

    fetch(url, { headers: { user_id: reqUserID, access_token: accessToken } })
      .then((res) => res.json())
      .then(
        (result) => {
          const surveys = result?.surveys || [];
          const locations = result?.locations || [];

          let data = this.state.data;
          if (this.state.cur_survey_id != null) {
            const cur = surveys.find((s) => s.id === this.state.cur_survey_id);
            data = cur ? this.extractSurveyDataFromItem(cur) : data;
          }

          this.setState({
            items: surveys,
            locations,
            data,
            isLoading: false,
          });
        },
        (error) => {
          this.setState({ items: [], locations: [], data: undefined, isLoading: false });
          this.showErrorMessage(ETVConstants.trans('error') || 'error', String(error));
        }
      );
  }

  // ---------- dialogs ----------
  showWindow(divID, show) {
    this.setState({ window: show ? divID : '' });
  }

  showInfoMessage(title, msg, isSpinning) {
    this.setState({
      window: 'message_div',
      info_type: 'info',
      info_title: title,
      info_message: msg,
      spinning: !!isSpinning,
    });
  }

  showSuccessMessage(title, msg) {
    this.setState({
      window: 'message_div',
      info_type: 'success',
      info_title: title,
      info_message: msg,
      spinning: false,
    });
  }

  showErrorMessage(title, msg) {
    this.setState({
      window: 'message_div',
      info_type: 'error',
      info_title: title,
      info_message: msg,
      spinning: false,
    });
  }

  closeInfoMessage() {
    this.setState({ window: '', info_type: '', info_title: '', info_message: '', spinning: false });
  }

  // ---------- actions ----------
  cmdSubmit(url, successTxt, failText, showSuccess = true) {
    const accessToken = localStorage.getItem('etv.access_token');
    const reqUserID = localStorage.getItem('etv.user_id');

    fetch(url, { headers: { user_id: reqUserID, access_token: accessToken } })
      .then((res) => res.json())
      .then(
        (result) => {
          if (result?.result) {
            if (showSuccess) this.showSuccessMessage(ETVConstants.trans('success') || 'success', successTxt);
            this.loadItems();
          } else {
            this.showErrorMessage(ETVConstants.trans('error') || 'error', failText);
          }
        },
        (error) => this.showErrorMessage(ETVConstants.trans('error') || 'error', String(error))
      );
  }

  resetSurvey(surveyID) {
    const url = `${ETVConstants.getServerBase()}/survey_mgmt?action=reset&survey_id=${encodeURIComponent(String(surveyID))}`;
    this.cmdSubmit(url, ETVConstants.trans('survey_resetted') || 'survey resetted', ETVConstants.trans('failure') || 'failure', false);
  }

  deleteSurvey(surveyID) {
    const url = `${ETVConstants.getServerBase()}/survey_mgmt?action=delete&survey_id=${encodeURIComponent(String(surveyID))}`;
    this.cmdSubmit(url, ETVConstants.trans('survey_deleted') || 'survey deleted', ETVConstants.trans('failure') || 'failure', false);
  }

  // ---------- create survey flow ----------
  createSatisfactionSurvey() { this.setState({ window: 'create_satisfaction_survey_div' }); }
  createGeneralSurvey() { this.setState({ window: 'create_general_survey_div' }); }

  // ---------- analysis / comments ----------
  extractSurveyDataFromItem(item) {
    const data = [];
    data[0] = ['Answer', 'Hits'];
    const answers = item?.ans || [];
    for (let i = 0; i < answers.length; i++) {
      const answer = String(answers[i]?.answer ?? '');
      const hits = Number(answers[i]?.hits ?? 0);
      data[i + 1] = [answer, hits];
    }
    return data;
  }

  analyzeSurvey(id) {
    const item = this.getItem(id);
    if (!item) return;
    const data = this.extractSurveyDataFromItem(item);
    this.setState({ cur_survey_id: id, question: item.question, data, chart_mode: 'bar' });
    this.showWindow('analyze_survey_div', true);
  }

  showComments(id) {
    const item = this.getItem(id);
    if (!item) return;

    const comments = (item.comments || []).filter((c) => typeof c === 'string' && c.trim().length > 0);
    const ordered = comments.slice().reverse();

    this.setState({ cur_survey_id: id, question: item.question, survey_comments: ordered });
    this.showWindow('comments_div', true);
  }

  createLanguageDropDown() {
    const languageNames = new Intl.DisplayNames([this.state.lang || 'en'], { type: 'language' });

    const formatLanguage = (code) => {
      try {
        const lang = String(code || '').split('-')[0];
        return languageNames.of(lang) || code;
      } catch {
        return code;
      }
    };

    return (
      <FormControl size="small" sx={{ minWidth: 260 }}>
        <InputLabel>{ETVConstants.trans('select_replies_by_language')}</InputLabel>
        <Select
          label={ETVConstants.trans('select_replies_by_language')}
          value={this.state.selected_language}
          onChange={(e) => this.setState({ selected_language: e.target.value }, () => this.loadItems())}
        >
          <MenuItem value="all">{ETVConstants.trans('all')}</MenuItem>
          {(this.state.locations || []).map((code) => (
            <MenuItem key={`lang_${code}`} value={code}>
              {formatLanguage(code)}
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                ({code})
              </Typography>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  async exportAnalysisAsPNG() {
    if (!this.analysisRef.current) return;
    const canvas = await html2canvas(this.analysisRef.current, { scale: 2, useCORS: true });
    const dataUrl = canvas.toDataURL('image/png', 1.0);
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `survey-analysis-${this.state.cur_survey_id || 'export'}.png`;
    a.click();
  }

  async exportAnalysisAsPDF() {
    if (!this.analysisRef.current) return;
    const canvas = await html2canvas(this.analysisRef.current, { scale: 2, useCORS: true });
    const imgData = canvas.toDataURL('image/png', 1.0);

    const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pageWidth - 60;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    const y = (pageHeight - imgHeight) / 2;

    pdf.addImage(imgData, 'PNG', 30, y, imgWidth, imgHeight);
    pdf.save(`survey-analysis-${this.state.cur_survey_id || 'export'}.pdf`);
  }

  render() {
    const columnsData = [
      {
        title: ETVConstants.trans('question'),
        field: 'question',
        width: 560,
        render: (rowData) => (
          <Box sx={{ minWidth: 0, pl: 1, pr: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap title={rowData.question}>
              {rowData.question}
            </Typography>
          </Box>
        ),
      },
      { title: ETVConstants.trans('hits'), field: 'total_hits', width: 70 },
      {
        title: ETVConstants.trans('analyze'),
        field: '',
        width: 75,
        render: (rowData) => (
          <Tooltip title={ETVConstants.trans('analyze')}><IconButton size="small" color="primary" onClick={() => this.analyzeSurvey(rowData.id)}><Analytics /></IconButton></Tooltip>
        ),
      },
      {
        title: ETVConstants.trans('comments'),
        field: '',
        width: 85,
        render: (rowData) => (
          <Tooltip title={ETVConstants.trans('comments')}><IconButton size="small" color="primary" onClick={() => this.showComments(rowData.id)}><Comment /></IconButton></Tooltip>
        ),
      },
      {
        title: ETVConstants.trans('reset'),
        field: '',
        width: 70,
        render: (rowData) => (
          <Tooltip title={ETVConstants.trans('reset')}><IconButton size="small" color="warning" onClick={() => this.resetSurvey(rowData.id)}><RestartAltIcon /></IconButton></Tooltip>
        ),
      },
      {
        title: ETVConstants.trans('delete'),
        field: 'select_layout',
        width: 70,
        render: (rowData) => (
          <Tooltip title={ETVConstants.trans('delete')}>
            <IconButton size="small" color="error" onClick={() => this.setState({window:'confirm_delete_survey',pending_delete:rowData})}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        ),
      },
    ];

    const chartRows = this.getChartRows();
    const totalHits = this.getTotalHits();
    const dataMax = this.getDataMax();
    const chartRowsEps = chartRows.map((r) => ({ ...r, hits: Number(r.hits || 0) }));

    return (
      <ETVPage width="wide" icon={<PollIcon/>} title={ETVConstants.trans('surveys_management') || 'surveys management'} loading={this.state.isLoading}
        topActions={
          <>
            <Button variant="contained" startIcon={<PollIcon />} onClick={() => this.createSatisfactionSurvey()}>
              {ETVConstants.trans('create_satisfaction_survey')}
            </Button>
            <Button variant="outlined" startIcon={<PollIcon />} onClick={() => this.createGeneralSurvey()}>
              {ETVConstants.trans('create_general_survey')}
            </Button>
          </>
        }>

            {this.state.items && this.state.items.length > 0 ? (
              <ETVTable data={this.state.items} columns={columnsData} title="" pageSize={10} search={false} embedded options={{padding:'dense',tableLayout:'fixed'}} />
            ) : (
              !this.state.isLoading && <Alert severity="info">{ETVConstants.trans('no_surveys_yet') || 'no surveys yet'}</Alert>
            )}

        {/* Message dialog */}
        <ETVMessagePopup
          type={this.state.spinning ? 'progress' : this.state.info_type}
          open={this.state.window === 'message_div'}
          onClose={() => this.closeInfoMessage()}
          title={this.state.info_title}
          message={this.state.info_message}
        />

        <ETVPopup open={this.state.window === 'confirm_delete_survey'} width="small"
          onClose={() => this.setState({window:'',pending_delete:null})}
          icon={<DeleteIcon color="error"/>} title={ETVConstants.trans('delete')}
          description={ETVConstants.trans('confirm_delete')}
          bottomActions={<>
            <ETVActionButton action="cancel" onClick={() => this.setState({window:'',pending_delete:null})}/>
            <Button color="error" variant="contained" startIcon={<DeleteIcon/>} onClick={() => {
              const id=this.state.pending_delete?.id;
              this.setState({window:'',pending_delete:null});
              if(id !== undefined) this.deleteSurvey(id);
            }}>{ETVConstants.trans('delete')}</Button>
          </>}>
          <Typography fontWeight={700}>{this.state.pending_delete?.question}</Typography>
        </ETVPopup>

        {/* Comments dialog */}
        <ETVPopup
          open={this.state.window === 'comments_div'}
          width="standard"
          onClose={() => this.setState({ window: '' })}
          icon={<Comment/>}
          title={ETVConstants.trans('comments') || 'comments'}
          description={`${(this.state.survey_comments || []).length}`}
          contentSx={{p:0}}
          bottomActions={<ETVActionButton action="cancel" label={ETVConstants.trans('close')} onClick={() => this.setState({window:''})}/>}>
            {!this.state.survey_comments || this.state.survey_comments.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {ETVConstants.trans('no_comments_yet') || 'no comments yet'}
                </Typography>
                <Typography variant="body2">
                  {ETVConstants.trans('comments_will_appear_here') || 'comments will appear here once users submit them.'}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ maxHeight: '65vh', overflow: 'auto' }}>
                <Stack divider={<Divider />} sx={{ p: 0 }}>
                  {(this.state.survey_comments || []).map((text, idx) => (
                    <Box key={`comment_${idx}`} sx={{ p: 2.5 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        {(ETVConstants.trans('comment') || 'comment')} #{idx + 1}
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                        {text}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
        </ETVPopup>

        {/* Template-based satisfaction surveys keep the existing server QR tracking. */}
        {this.state.window === 'create_satisfaction_survey_div' && <SurveyCreationWizard user_id={this.props.user_id} onClose={() => this.setState({ window: '' })} onSaved={() => {
          this.showSuccessMessage(ETVConstants.trans('survey') || 'Survey', ETVConstants.trans('survey_registered') || 'Survey registered');
          this.loadItems();
        }} />}

        {this.state.window === 'create_general_survey_div' && <SurveyCreationWizard surveyType="general" user_id={this.props.user_id} onClose={() => this.setState({ window: '' })} onSaved={() => {
          this.showSuccessMessage(ETVConstants.trans('survey') || 'Survey', ETVConstants.trans('survey_registered') || 'Survey registered');
          this.loadItems();
        }} />}

        {/* Analyze dialog */}
        <ETVPopup
          open={this.state.window === 'analyze_survey_div'}
          width="standard"
          onClose={() => this.setState({ window: '' })}
          icon={<BarChartIcon/>}
          title={ETVConstants.trans('analysis') || 'analysis'}
          bottomActions={<>
            <Button variant="outlined" startIcon={this.state.chart_mode === 'bar' ? <DonutLargeIcon /> : <StackedBarChartIcon />}
              onClick={() => this.toggleChartMode()} disabled={totalHits === 0}>
              {this.state.chart_mode === 'bar' ? ETVConstants.trans('donut') || 'donut' : ETVConstants.trans('bar') || 'bar'}
            </Button>
            <Button variant="outlined" startIcon={<IosShareIcon />} onClick={() => this.exportAnalysisAsPNG()}>PNG</Button>
            <Button variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={() => this.exportAnalysisAsPDF()}>PDF</Button>
            <ETVActionButton action="cancel" label={ETVConstants.trans('close')} onClick={() => this.setState({window:''})}/>
          </>}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              {this.state.question}
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" sx={{ mb: 2 }}>
              {this.createLanguageDropDown()}
            </Stack>

            {this.state.data ? (
              <Box ref={this.analysisRef}>
                <Stack direction="row" spacing={1.5} sx={{ mb: 2, flexWrap: 'wrap' }}>
                  <Chip label={`total hits: ${totalHits}`} color="primary" variant="outlined" />
                  {this.getLeader() ? (
                    <Chip
                      label={`top answer: ${this.getLeader().name} (${this.getLeader().hits})`}
                      color="success"
                      variant="outlined"
                    />
                  ) : null}
                </Stack>

                {totalHits === 0 ? (
                  <Stack spacing={2} sx={{ mt: 1 }}>
                    <Box
                      sx={{
                        p: 3,
                        borderRadius: 2,
                        border: (t) => `1px dashed ${t.palette.divider}`,
                        textAlign: 'center',
                        bgcolor: 'background.default',
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                        {ETVConstants.trans('no_responses_yet') || 'no responses yet'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {ETVConstants.trans('survey_waiting_for_votes') ||
                          'this survey is waiting for votes. as soon as results come in, charts will appear here.'}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        height: 300,
                        width: '100%',
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        border: (t) => `1px solid ${t.palette.divider}`,
                      }}
                    >
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartRowsEps} layout="vertical" margin={{ top: 16, right: 24, left: 8, bottom: 16 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" domain={[0, 1]} allowDecimals={false} />
                          <YAxis type="category" dataKey="name" width={140} />
                          <ReTooltip formatter={(v) => [v, ETVConstants.trans('hits')]} labelFormatter={(l) => `${l}`} />
                          <Bar dataKey="hits" radius={[6, 6, 6, 6]}>
                            {chartRowsEps.map((_, idx) => (
                              <Cell key={`bar0-${idx}`} fill={COLORS[idx % COLORS.length]} />
                            ))}
                            <LabelList dataKey="hits" position="right" style={{ fontWeight: 600 }} />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </Stack>
                ) : this.state.chart_mode === 'bar' ? (
                  <Box
                    sx={{
                      height: 420,
                      width: '100%',
                      bgcolor: 'background.paper',
                      borderRadius: 2,
                      border: (t) => `1px solid ${t.palette.divider}`,
                    }}
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartRowsEps} layout="vertical" margin={{ top: 16, right: 24, left: 8, bottom: 16 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" domain={[0, Math.max(1, dataMax)]} allowDecimals={false} />
                        <YAxis type="category" dataKey="name" width={140} />
                        <ReTooltip formatter={(v) => [v, ETVConstants.trans('hits')]} labelFormatter={(l) => `${l}`} />
                        <Bar dataKey="hits" radius={[6, 6, 6, 6]}>
                          {chartRowsEps.map((_, idx) => (
                            <Cell key={`bar-${idx}`} fill={COLORS[idx % COLORS.length]} />
                          ))}
                          <LabelList dataKey="hits" position="right" style={{ fontWeight: 600 }} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      height: 420,
                      width: '100%',
                      bgcolor: 'background.paper',
                      borderRadius: 2,
                      border: (t) => `1px solid ${t.palette.divider}`,
                    }}
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={chartRows} dataKey="hits" nameKey="name" innerRadius="55%" outerRadius="85%" paddingAngle={2}>
                          {chartRows.map((_, idx) => (
                            <Cell key={`slice-${idx}`} fill={COLORS[idx % COLORS.length]} />
                          ))}
                          <LabelList dataKey="hits" position="outside" style={{ fontWeight: 600 }} formatter={(v) => `${v}`} />
                        </Pie>
                        <Legend />
                        <ReTooltip
                          formatter={(v) => [
                            `${v} (${Math.round((v / Math.max(1, totalHits)) * 100)}%)`,
                            ETVConstants.trans('hits'),
                          ]}
                          labelFormatter={(l) => `${l}`}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                )}
              </Box>
            ) : (
              <Box sx={{ py: 4, textAlign: 'center' }}>{ETVConstants.trans('loading_chart') || 'loading chart'}</Box>
            )}
        </ETVPopup>
      </ETVPage>
    );
  }
}

export default SurveyMgmt;
